import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { AboutNode, Details, Modal, Search } from '@/components'
import { useApp } from '@/hooks'
import { t } from '@/i18n'
import { ICustomer, IDriver } from '@/types'
import { carsPath, makeSearchPath } from '@/utils'

import { useData as useDriverData } from '../Drivers/useData'
import { useData as useCustomerData } from '../Main/useData'
import { SelectedType } from './components/constants'
// import CarDetails from './components/carDetails'
// import CarHistory from './components/carHistory'
import FileListButton from '@/components/buttons/FileListButton'
import FileUploadButton from '@/components/buttons/FileUploadButton'
import useIsMobile from '@/hooks/useIsMobile'
import { compact } from 'lodash'
import History from '../Car/history'
import CarRow from './components/CarRow'
import EditCar from './components/editCar'
import EditCustomerForm from './editCutomer'
import EditDriverForm from './editDriverForm'
import { useData } from './useData'
import { useSelect } from './useSelect'
import { buildData } from './utils'
import { useCanUpdate } from '@/hooks'

const Cars: React.FC = () => {
  const { isRTL } = useApp()
  const navigate = useNavigate()
  const { id, customerId } = useParams()
  const [showHistory, setShowHistory] = useState<boolean>(false)
  const [editing, setEditing] = useState(false)
  const _customerId = useMemo(
    () => (customerId === 'customer' ? undefined : Number(customerId)),
    [customerId],
  )

  const isMobile = useIsMobile()
  const [selectedType, setType] = useState<SelectedType | null>(null)
  const editCarRef = useRef<React.ElementRef<typeof EditCar> | null>(null)
  const { data, loading, onFetchMore, onSearch, pageInfo } =
    useSelect(_customerId)
  const { data: selectedData, loading: loadingDetail } = useData({ id })
  const {
    loading: driverLoading,
    submitting: driverSubmitting,
    onSubmit: onDriverFormSubmit,
  } = useDriverData({
    id: selectedData?.driver?.id,
  })
  const {
    submitting,
    onSubmit,
    loading: customerLoading,
  } = useCustomerData({
    id: _customerId?.toString(),
  })

  const onClose = useCallback(() => {
    setType(null)
    setEditing(false)
    navigate(makeSearchPath(carsPath, { customerId: _customerId }))
  }, [navigate, _customerId])

  const HeaderComponent = useMemo(
    () => <Search onSearch={onSearch} />,
    [onSearch],
  )

  const goBack = useCallback(
    (type: SelectedType | null) => {
      if (editing) {
        setEditing(false)
        return
      }
      setType(type)
      setEditing(false)
    },
    [editing],
  )

  const onSubmitCustomerForm = useCallback(
    (d: ICustomer) => {
      onSubmit(`customers/customers/${id}/`, true, () => setEditing(false))(d)
    },
    [id, onSubmit],
  )

  const onSubmitDriverForm = useCallback(
    (d: IDriver) => {
      onDriverFormSubmit(`cars/drivers/${selectedData?.driver?.id}`, true, () =>
        setEditing(false),
      )({
        driver: d,
        driver_id: selectedData?.driver?.id,
      })
    },
    [selectedData?.driver?.id, onDriverFormSubmit],
  )

  const isDriver = useMemo(
    () => selectedType === SelectedType.Driver,
    [selectedType],
  )

  const isCustomer = useMemo(
    () => selectedType === SelectedType.Customer,
    [selectedType],
  )

  const details = useMemo(
    () =>
      selectedData
        ? buildData(selectedData, isRTL)[
            isDriver ? 'driver' : isCustomer ? 'customer' : 'general_info'
          ]
        : [],
    [selectedData, isDriver, isCustomer, isRTL],
  )

  const canUpdate = useCanUpdate()

  const sections = useMemo(() => {
    if (editing) return []
    return [
      {
        data: details,
        title: isCustomer ? 'customer' : isDriver ? 'driver' : 'general_info',
        headerButtons: editing
          ? undefined
          : compact([
              {
                children: (
                  <>
                    <span className="icon-history"></span>
                    {t('car_positions')}
                  </>
                ),
                className: 'main-btn green',
                onClick: () => setShowHistory(true),
              },
              canUpdate && {
                children: (
                  <>
                    <span className="icon-edit"></span>
                    {t('edit')}
                  </>
                ),
                className: 'main-btn outline',
                onClick: () =>
                  isDriver || isCustomer
                    ? setEditing(true)
                    : editCarRef.current?.edit(true),
              },
              {
                component: FileListButton,
                props: {
                  contextType: 'car',
                  contextId: id || '',
                },
              },
              !isMobile && {
                component: FileUploadButton,
                props: {
                  contextType: 'car',
                  contextId: id || '',
                },
              },
              ...(isDriver
                ? [
                    {
                      children: (
                        <>
                          <span className="icon-delete"></span>
                          {t('delete')}
                        </>
                      ),
                      className: 'main-btn red',
                      onClick: () => editCarRef.current?.edit(true),
                    },
                  ]
                : []),
            ]),
      },
    ]
  }, [editing, details, isCustomer, isDriver, isMobile, canUpdate])

  return (
    <>
      <div className="row">
        <div className={`col w-${!id ? '100' : '45'}`}>
          <Details
            showNoData
            data={data}
            Row={CarRow}
            loading={loading}
            title={t('cars')}
            TitleWrapper="h4"
            onFetchMore={onFetchMore}
            wrapperClassName="info-box w-100"
            HeaderComponent={HeaderComponent}
            dataBoxWrapperClassName="scroll-wrap"
            listWrapperClassName="cars-list item"
            fetchMoreStatus={pageInfo.fetchMoreStatus}
          />
        </div>
        <div className="col w-55">
          <AboutNode
            loading={loadingDetail}
            sections={sections}
            node={id ? selectedData : undefined}
            goBackTitle={
              isDriver
                ? editing
                  ? 'driver'
                  : 'car'
                : isCustomer
                ? editing
                  ? 'customer'
                  : 'driver'
                : undefined
            }
            goBack={
              selectedType
                ? () => goBack(isDriver ? null : SelectedType.Driver)
                : undefined
            }
            onClose={onClose}
            HeaderComponent={
              <>
                {/* {!selectedType && (
                  <>
                    <CarDetails node={selectedData} />
                    <CarHistory setType={setType} node={selectedData} />
                  </>
                )} */}
                {editing && (
                  <>
                    {isDriver && (
                      <EditDriverForm
                        details={details}
                        isEditing={true}
                        loading={driverLoading}
                        submitting={driverSubmitting}
                        onSubmit={onSubmitDriverForm}
                        onCancel={() => setEditing(false)}
                        // @ts-ignore
                        initialData={selectedData?.driver}
                      />
                    )}
                    {isCustomer && (
                      <EditCustomerForm
                        details={details}
                        isEditing={true}
                        submitting={submitting}
                        loading={customerLoading}
                        onSubmit={onSubmitCustomerForm}
                        onCancel={() => setEditing(false)}
                        initialData={selectedData?.driver?.customer}
                      />
                    )}
                  </>
                )}
              </>
            }
          />
        </div>
      </div>
      <EditCar ref={editCarRef} node={selectedData} />
      {/* <MovementsHistory ref={movementsRef} /> */}
      {showHistory && selectedData?.id && (
        <Modal
          className="car-positions"
          visible
          title={t('car_positions')}
          onClose={() => setShowHistory(false)}
        >
          <History id={selectedData.id} carId={selectedData.id} />
        </Modal>
      )}
    </>
  )
}

export default Cars
